import { useState, MouseEvent } from "react";
import styles from "./InlineDropDown.module.scss";

interface OptionsMap {
	value: string;
	label: string;
}

interface InlineDropDownProps {
    options: OptionsMap[];
    setExternalState: (x: string) => void;
    preselectedValue?: string;
    maxItems?: number;
}

/**
 * Generates the click event handler on an anchor element in the InlineDropDown component.
 *
 * @param x - The selected option from the dropdown menu.
 * @param setExternalState - A function to set the external state with the value of the selected option.
 * @param setSelected - A function to set the state of the selected option in the component.
 *
 * @returns An event handler function that takes a React.MouseEvent<HTMLAnchorElement> event as its parameter.
 */
const generateClickHandler = (
    x: OptionsMap,
    setExternalState: (x: string) => void,
    setSelected: (x: string) => void
) => (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setExternalState(x.value);
    setSelected(x.label);
    (e.target as HTMLAnchorElement)?.parentElement?.parentElement?.removeAttribute("open");
};

/**
 * Renders a dropdown menu component.
 *
 * @param {Object} props - The component props.
 * @param {Array} props.options - An array of objects representing the dropdown options. Each object should have a `value` and a `label` property.
 * @param {Function} props.setExternalState - A function that will be called with the selected option's value when it changes.
 * @param {string} [props.preselectedValue=""] - The value of the option that should be preselected when the component is rendered.
 * @returns {JSX.Element} - The rendered dropdown menu component.
 */
const InlineDropDown = (props: InlineDropDownProps) => {
    const { options, setExternalState, preselectedValue = "", maxItems = null } = props;
    const [selected, setSelected] = useState<string>(preselectedValue);

    if (options.length === 1) {
        return <span className={styles.label}>{selected}{" "}</span>;
    }

    const maxHeight = maxItems ? `${maxItems * 37}px` : "auto";
    const overflow = maxItems ? "scroll" : "auto";

    return (
        <details className={styles.InlineDropDown}>
            <summary>
                <span className={styles.label}>{selected}</span>
                {" "}
                <svg className={styles.arrow} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 12 13" width="1em" height="1em" >
                    <path fill="#0A66C2" d="m6 10.816 6-6-1.294-1.294L6 8.11 1.294 3.522 0 4.816l6 6Z" />
                </svg>
            </summary>
            <div className={styles.list} style={{ maxHeight: maxHeight, overflowY: overflow }}>
                {options.map((x: OptionsMap) => {
                    const isActive = selected === x.label;
                    const onClick = generateClickHandler(x, setExternalState, setSelected);
                    return (
                        <a key={x.value} data-active={isActive} href="" onClick={onClick}>{x.label}</a>
                    );
                })}
            </div>
        </details>
    );
};
InlineDropDown.displayName = "InlineDropDown";

export default InlineDropDown;
export type { InlineDropDownProps };
